import type { ImgHTMLAttributes } from "react";
import { useState } from "react";

import { BriefEntryLink } from "@/components/marketing/brief-entry-link";
import { buttonVariants } from "@/components/ui/button";
import { getSupportEmail } from "@/lib/support";
import { packageTypeForLandingPlan } from "@/lib/landing-package-map";
import { cn } from "@/lib/utils";

const pricingPlans = [
    {
        key: "Landing page",
        price: "$99.00",
        subtitle: "Ready by tomorrow. Stunning single-page website",
        features: [
            "Stunning single-page website",
            "Mobile-responsive design",
            "Contact form integration",
            "Basic SEO optimization",
            "Free Netlify hosting",
            "1 round of revisions",
        ],
    },
    {
        key: "Branding Package",
        price: "$149",
        subtitle: "Brand system pack for fast launch consistency",
        features: [
            "Logo and visual identity base",
            "Color and typography style guide",
            "Core social media asset kit",
            "Landing page visual direction",
            "Figma source handoff",
            "2 rounds of revisions",
        ],
    },
    {
        key: "Basic MVP",
        price: "$499",
        subtitle: "Functional MVP to validate your idea quickly",
        features: [
            "Auth + database setup",
            "Core dashboard screens",
            "Basic payment integration",
            "Responsive front-end",
            "Deployment-ready code",
            "3 rounds of revisions"
        ],
    },
    {
        key: "Advanced MVP",
        price: "$1499",
        subtitle: "Expanded MVP with more flows and polish",
        features: [
            "Everything in Basic MVP",
            "Advanced workflow screens",
            "Role-based access controls",
            "Deeper Stripe setup",
            "Analytics-ready structure",
            "Priority delivery support",
            "4 rounds of revisions",
        ],
    },
] as const;

type ImageProps = ImgHTMLAttributes<HTMLImageElement> & { priority?: boolean };

function Image({ priority: _priority, ...props }: ImageProps) {
  return <img {...props} />;
}

export function Price() {
  const [activePlan, setActivePlan] =
    useState<(typeof pricingPlans)[number]["key"]>("Landing page");
  const currentPlan =
    pricingPlans.find((plan) => plan.key === activePlan) ?? pricingPlans[0];

  return (
    <section
      id="pricing"
      className="mx-auto w-full max-w-6xl px-6 py-10 md:py-30 md:px-12 lg:px-20"
    >
      <div className="mx-auto flex w-full max-w-[360px] sm:max-w-[740px] flex-col items-center gap-[clamp(20px,4vw,40px)]">
        <div
          className="
    flex items-center gap-[6px]
    rounded-[50px]
    border border-[#F3F3F3]
    bg-[#ffffff]
    py-[5px] px-[13px]
    shadow-[
      1px_0px_2px_0px_rgba(0,0,0,0.03),
      2px_2px_3px_0px_rgba(0,0,0,0.03),
      5px_4px_4px_0px_rgba(0,0,0,0.02),
      9px_8px_5px_0px_rgba(0,0,0,0),
      14px_12px_5px_0px_rgba(0,0,0,0)
    ]
  "
        >
          <span className="font-sans text-[13px] font-normal leading-none tracking-[-0.02em] text-[#000]">
            Pricing
          </span>
          {/* <span className="flex items-center justify-center rounded-[30px] bg-[#f8f9fb] px-[10px] py-[8px] font-sans text-[13px] font-normal leading-none tracking-[-0.02em] text-[#000000]">
                        best pricing for best people
                    </span> */}
        </div>
        <h2 className="max-w-[500px] text-center font-plus-jakarta-extrabold text-[40px] md:text-[clamp(40px,4.5vw,60px)] font-bold leading-none tracking-[-0.02em] text-[#000000]">
          Simple, best flat-
          <br className="md:hidden" />
          rate pricing.
        </h2>
        <p className="text-center font-sans text-[16px] md:text-[15px] font-medium leading-[140%] tracking-[0%] text-[#716C77]">
          No hidden fees. No hourly rates. What you see is what you pay.
        </p>
      </div>

      <div className="mx-auto mt-[clamp(40px,6vw,74px)] w-full max-w-[960px] rounded-[40px] border border-[#E4E2EB] bg-white p-[10px]">
        <div className="flex w-full flex-col p-[20px] md:p-[50px] rounded-[30px] bg-[#FAFAFA]">
          <div
            id="pricing-tabs"
            className="scroll-mt-[100px] flex flex-wrap justify-center gap-[10px] [@media(max-width:450px)]:flex-col [@media(max-width:450px)]:gap-[6px]"
          >
            {pricingPlans.map((item) => {
              const isActive = activePlan === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setActivePlan(item.key)}
                  className="flex h-[58px] w-fit items-center justify-center rounded-[100px] border border-[#E4E2EA] bg-white p-[5px] transition-all duration-300 hover:scale-[1.05] active:scale-[0.95] hover:shadow-md cursor-pointer [@media(max-width:450px)]:w-full"
                >
                  <span
                    className={`flex h-[48px] w-full items-center justify-center rounded-[100px] px-[30px] md:px-[34px] cursor-pointer font-sans text-[13px] font-normal leading-none tracking-[0%] transition-all duration-300 ${
                      isActive
                        ? "bg-[linear-gradient(0deg,#18171A_0%,#5E5C65_100%)] text-white"
                        : "bg-transparent text-[#18171A] hover:bg-[linear-gradient(0deg,#18171A_0%,#5E5C65_100%)] hover:text-white"
                    }`}
                  >
                    {item.key}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-[clamp(30px,5vw,50px)]">
            <div className="grid gap-6 md:grid-cols-[1fr_auto] lg:grid-cols-[1fr_400px]">
              <div className="flex flex-col h-full text-left">
                <div>
                  <p
                    className="text-[18px] font-medium leading-none text-[#000000] mb-[25px]"
                    style={{ fontFamily: "var(--font-geist)" }}
                  >
                    {currentPlan.key}
                  </p>
                  <div className="mb-[25px] flex items-end gap-1 justify-start">
                    <p
                      className="text-[40px] font-semibold leading-none text-[#000000]"
                      style={{ fontFamily: "var(--font-geist)" }}
                    >
                      {currentPlan.price}
                    </p>
                    <p
                      className="mb-[4px] text-[16px] font-normal leading-[1.2] text-[#716C77]"
                      style={{ fontFamily: "var(--font-geist)" }}
                    >
                      / One-time
                    </p>
                  </div>
                  <p
                    className="text-[14px] font-normal leading-[1.2] text-[#716C77]"
                    style={{ fontFamily: "var(--font-geist)" }}
                  >
                    {currentPlan.subtitle}
                  </p>
                </div>
                <div className="relative mt-[40px] hidden h-[58px] w-full items-center justify-start md:flex">
                  <div className="absolute left-0 right-0 h-[2px] w-full bg-[linear-gradient(90deg,#E4E2EA_16.815%,#FFFFFF_100%)] md:block"></div>
                  {packageTypeForLandingPlan(currentPlan.key) ? (
                    <BriefEntryLink
                      package={packageTypeForLandingPlan(currentPlan.key)!}
                      className={cn(
                        "relative z-10 flex h-[58px] w-[170px] cursor-pointer items-center justify-center no-underline",
                        "rounded-[100px] border border-[#e4e2ea] bg-white p-[5px] transition-all duration-300 hover:scale-[1.05] active:scale-[0.95] hover:shadow-xl group",
                      )}
                    >
                      <span
                        className="
        flex h-[48px] w-[160px] items-center justify-center gap-[14px]
        rounded-[40px]
        bg-[linear-gradient(0deg,#18171A_0%,#5E5C65_100%)]
        text-[13px] text-white
        transition-all duration-300
        group-hover:brightness-110
      "
    >
      <Image
        src="/Images/thinder.svg"
        width={9}
        height={13}
        alt=""
      />

      Get started
    </span>
  </BriefEntryLink>
) : null}
                                        </div>
                                    </div>

              <div
                className="rounded-[15px] border border-[rgba(228,226,235,0.6)] bg-white p-[25px] self-start"
                style={{
                  boxShadow:
                    "-2px 3px 7px 0px #00000008, -6px 10px 12px 0px #00000008, -14px 23px 16px 0px #00000005, -26px 42px 20px 0px #00000000, -40px 65px 21px 0px #00000000",
                }}
              >
                <ul className="space-y-[16px]">
                  {currentPlan.features.map((item) => (
                    <li key={item} className="flex items-center gap-4">
                      <Image
                        src="/Images/footer/tickicon.svg"
                        alt="Tick"
                        width={20}
                        height={20}
                        className="h-[20px] w-[20px] shrink-0"
                      />
                      <span
                        className="text-[14px] font-medium leading-none text-[#000000]"
                        style={{ fontFamily: "var(--font-geist)" }}
                      >
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

                                    <div className="mt-8 flex w-full items-center justify-center md:hidden">
                                        {packageTypeForLandingPlan(currentPlan.key) ? (
                                          <BriefEntryLink
                                            package={packageTypeForLandingPlan(currentPlan.key)!}
                                            className={cn(
                                              buttonVariants({ variant: "default", size: "default" }),
                                              "relative z-10 w-full cursor-pointer no-underline gap-3.5 h-12 flex items-center justify-center rounded-[40px] bg-[linear-gradient(0deg,#18171A_0%,#5E5C65_100%)] px-[35px] text-[13px] text-white",
                                            )}
                                          >
                                            <Image
                                              src="/Images/thinder.svg"
                                              width={9}
                                              height={13}
                                              alt=""
                                            />
                                            Get started
                                          </BriefEntryLink>
                                        ) : null}
                                    </div>
                                </div>
                    </div>
                </div>
            </div>
            <div className="mx-auto mt-[15px] md:mt-[55px] max-w-[673px] w-full rounded-[15px] border border-[#e4e2eb] bg-white p-[8px] shadow-[0_12px_26px_rgba(0,0,0,0.08)]">
                <div className="flex flex-wrap items-center justify-between gap-[20px] rounded-[10px] bg-[#fafafa] pt-[20px] pr-[30px] pb-[20px] pl-[40px] [@media(max-width:450px)]:p-[20px]">
                    <div className="flex flex-row items-center gap-[20px] w-full md:w-auto">
                        <Image
                            src="/Images/footer/avatar.svg"
                            alt="Call with team member"
                            width={55}
                            height={55}
                            className="h-[55px] w-[55px] shrink-0 object-cover rounded-full"
                            style={{ boxShadow: "0px 1.25px 2.5px 0px #0000001A, 0px 5px 5px 0px #00000017, 0px 10px 6.25px 0px #0000000D, 0px 18.75px 7.5px 0px #00000003, 0px 28.75px 7.5px 0px #00000000" }}
                        />
                        <div className="flex flex-col gap-[12px] md:gap-[15px] text-left">
                            <p 
                                className="text-[20px] [@media(max-width:450px)]:text-[16px] font-medium leading-none text-[#000000]"
                                style={{ fontFamily: 'var(--font-geist)' }}
                            >
                                Book a call
                            </p>
                            <p 
                                className="text-[14px] [@media(max-width:450px)]:text-[13px] font-normal leading-[1.2] [@media(max-width:450px)]:leading-[1.1] text-[#716c77]"
                                style={{ fontFamily: 'var(--font-geist)' }}
                            >
                                Book a 15-minute free call with Tania Jennifer
                            </p>
                        </div>
                    </div>
                    <div className="p-[5px] bg-[#fff] rounded-full border border-[#E4E2EA]">
                   <a
  href={`mailto:${getSupportEmail()}`}
  className={cn(
    buttonVariants({ variant: "default", size: "default" }),
    "inline-flex w-full cursor-pointer items-center justify-center py-[23px] px-[35px] gap-[14px] no-underline md:w-auto bg-[linear-gradient(0deg,_#18171A_0%,_#5E5C65_100%)]"
  )}
>
                        <Image src="/Images/NavbarIconButton.svg" alt="" width={10} height={12} />
                        Book a call
                    </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
